import React from 'react';
import { ArrowLeft, Shield, Lock, Eye, Trash2, FileText } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import SEO from '../components/SEO';

const PolitiqueConfidentialite = () => {
  return (
    <>
      <SEO 
        title="Politique de Confidentialité - Orion Industrie"
        description="Politique de confidentialité RGPD d'Orion Industrie. Protection de vos données personnelles, vos droits et nos engagements."
        url="https://orion-industrie.eu/politique-confidentialite"
      />
      
      <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-slate-900 text-white py-6">
        <div className="container mx-auto px-4">
          <Link to="/">
            <Button variant="ghost" className="text-white hover:text-orange-400 mb-4">
              <ArrowLeft className="mr-2 w-4 h-4" />
              Retour à l'accueil
            </Button>
          </Link>
          <h1 className="text-4xl font-bold">Politique de Confidentialité</h1>
          <p className="text-gray-300 mt-2">Conformité RGPD - Protection de vos données personnelles</p>
        </div>
      </header>

      {/* Content */}
      <div className="container mx-auto px-4 py-12">
        <div className="max-w-4xl mx-auto bg-white rounded-xl shadow-lg p-8 md:p-12">
          
          {/* Introduction */}
          <section className="mb-8">
            <div className="bg-orange-50 border-l-4 border-orange-500 p-6 rounded-lg mb-6">
              <p className="text-gray-700 leading-relaxed">
                Orion Industrie s'engage à protéger la vie privée de ses utilisateurs et clients. Cette politique de confidentialité 
                explique comment nous collectons, utilisons et protégeons vos données personnelles conformément au 
                <strong> Règlement Général sur la Protection des Données (RGPD)</strong> et à la loi Informatique et Libertés.
              </p>
            </div>
          </section>

          {/* Responsable du traitement */}
          <section className="mb-8">
            <h2 className="text-2xl font-bold text-slate-900 mb-4 border-b-2 border-orange-500 pb-2 flex items-center">
              <Shield className="mr-2 text-orange-500" />
              1. Responsable du traitement des données
            </h2>
            <div className="text-gray-700">
              <p className="mb-2"><strong>Responsable :</strong> Benoit Manin</p>
              <p className="mb-2"><strong>Entreprise :</strong> Orion Industrie</p>
              <p className="mb-2"><strong>Adresse :</strong> 2035 route de Villars, 58490 Saint Parize le Chatel</p>
              <p className="mb-2"><strong>Email :</strong> gestion@orion-industrie.eu</p>
              <p className="mb-2"><strong>Téléphone :</strong> 06 59 45 25 62</p>
            </div>
          </section>

          {/* Données collectées */}
          <section className="mb-8">
            <h2 className="text-2xl font-bold text-slate-900 mb-4 border-b-2 border-orange-500 pb-2 flex items-center">
              <FileText className="mr-2 text-orange-500" />
              2. Données personnelles collectées
            </h2>
            <p className="text-gray-700 mb-4">
              Nous collectons les données personnelles suivantes lorsque vous utilisez nos formulaires de contact ou de demande de devis :
            </p>
            
            <div className="bg-gray-50 p-6 rounded-lg mb-4">
              <h3 className="font-bold text-slate-900 mb-3">📋 Via le formulaire de contact :</h3>
              <ul className="list-disc list-inside text-gray-700 space-y-1">
                <li>Nom et prénom</li>
                <li>Adresse email</li>
                <li>Numéro de téléphone</li>
                <li>Message</li>
              </ul>
            </div>

            <div className="bg-gray-50 p-6 rounded-lg">
              <h3 className="font-bold text-slate-900 mb-3">📋 Via le formulaire de demande de devis :</h3>
              <ul className="list-disc list-inside text-gray-700 space-y-1">
                <li>Nom de l'entreprise</li>
                <li>Nom et prénom</li>
                <li>Adresse email</li>
                <li>Numéro de téléphone</li>
                <li>Hauteur de quai</li>
                <li>Type d'équipement souhaité</li>
                <li>Message</li>
              </ul>
            </div>
          </section>

          {/* Finalité */}
          <section className="mb-8">
            <h2 className="text-2xl font-bold text-slate-900 mb-4 border-b-2 border-orange-500 pb-2 flex items-center">
              <Eye className="mr-2 text-orange-500" />
              3. Finalité du traitement
            </h2>
            <p className="text-gray-700 mb-3">
              Les données personnelles collectées sont utilisées uniquement pour les finalités suivantes :
            </p>
            <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
              <li>Traiter et répondre à vos demandes de contact et de devis</li>
              <li>Vous fournir des informations sur nos produits et services</li>
              <li>Améliorer la qualité de nos services</li>
              <li>Respecter nos obligations légales et réglementaires</li>
            </ul>
            <div className="mt-4 p-4 bg-blue-50 rounded-lg border-l-4 border-blue-500">
              <p className="text-gray-700 text-sm">
                <strong>Base légale :</strong> Votre consentement (en soumettant le formulaire) et l'exécution de mesures précontractuelles.
              </p>
            </div>
          </section>

          {/* Durée de conservation */}
          <section className="mb-8">
            <h2 className="text-2xl font-bold text-slate-900 mb-4 border-b-2 border-orange-500 pb-2 flex items-center">
              <Trash2 className="mr-2 text-orange-500" />
              4. Durée de conservation
            </h2>
            <p className="text-gray-700">
              Vos données personnelles sont conservées pendant une durée de <strong>3 ans</strong> à compter de votre dernier contact 
              avec nous ou de votre dernière demande de devis. Passé ce délai, vos données sont supprimées de nos systèmes, 
              sauf obligation légale de conservation plus longue.
            </p>
          </section>

          {/* Destinataires */}
          <section className="mb-8">
            <h2 className="text-2xl font-bold text-slate-900 mb-4 border-b-2 border-orange-500 pb-2">
              5. Destinataires des données
            </h2>
            <p className="text-gray-700 mb-3">
              Vos données personnelles sont destinées :
            </p>
            <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
              <li>Au personnel interne d'Orion Industrie pour le traitement de vos demandes</li>
              <li>À notre hébergeur (Emergent AI, Inc.) dans le cadre de l'hébergement sécurisé de notre site</li>
            </ul>
            <div className="mt-4 p-4 bg-green-50 rounded-lg border-l-4 border-green-500">
              <p className="text-gray-700 text-sm">
                <strong>Aucune vente de données :</strong> Nous ne vendons, ne louons ni ne partageons vos données personnelles 
                avec des tiers à des fins commerciales.
              </p>
            </div>
          </section>

          {/* Droits RGPD */}
          <section className="mb-8">
            <h2 className="text-2xl font-bold text-slate-900 mb-4 border-b-2 border-orange-500 pb-2 flex items-center">
              <Lock className="mr-2 text-orange-500" />
              6. Vos droits (RGPD)
            </h2>
            <p className="text-gray-700 mb-4">
              Conformément au RGPD, vous disposez des droits suivants concernant vos données personnelles :
            </p>
            
            <div className="grid md:grid-cols-2 gap-4">
              <div className="bg-gray-50 p-4 rounded-lg">
                <h3 className="font-bold text-slate-900 mb-2">✅ Droit d'accès</h3>
                <p className="text-gray-700 text-sm">Obtenir une copie de vos données</p>
              </div>
              <div className="bg-gray-50 p-4 rounded-lg">
                <h3 className="font-bold text-slate-900 mb-2">✏️ Droit de rectification</h3>
                <p className="text-gray-700 text-sm">Corriger vos données inexactes</p>
              </div>
              <div className="bg-gray-50 p-4 rounded-lg">
                <h3 className="font-bold text-slate-900 mb-2">🗑️ Droit à l'effacement</h3>
                <p className="text-gray-700 text-sm">Supprimer vos données (droit à l'oubli)</p>
              </div>
              <div className="bg-gray-50 p-4 rounded-lg">
                <h3 className="font-bold text-slate-900 mb-2">🚫 Droit d'opposition</h3>
                <p className="text-gray-700 text-sm">Vous opposer au traitement</p>
              </div>
              <div className="bg-gray-50 p-4 rounded-lg">
                <h3 className="font-bold text-slate-900 mb-2">📦 Droit à la portabilité</h3>
                <p className="text-gray-700 text-sm">Récupérer vos données dans un format structuré</p>
              </div>
              <div className="bg-gray-50 p-4 rounded-lg">
                <h3 className="font-bold text-slate-900 mb-2">⏸️ Droit à la limitation</h3>
                <p className="text-gray-700 text-sm">Limiter le traitement de vos données</p>
              </div>
            </div>

            <div className="mt-6 p-6 bg-orange-50 rounded-lg border-l-4 border-orange-500">
              <h3 className="font-bold text-slate-900 mb-2">📧 Comment exercer vos droits ?</h3>
              <p className="text-gray-700 mb-2">
                Pour exercer l'un de ces droits, contactez-nous :
              </p>
              <p className="text-gray-700"><strong>Email :</strong> gestion@orion-industrie.eu</p>
              <p className="text-gray-700"><strong>Téléphone :</strong> 06 59 45 25 62</p>
              <p className="text-gray-700 text-sm mt-3">
                Nous nous engageons à répondre à votre demande dans un délai de <strong>1 mois</strong> maximum.
              </p>
            </div>
          </section>

          {/* Sécurité */}
          <section className="mb-8">
            <h2 className="text-2xl font-bold text-slate-900 mb-4 border-b-2 border-orange-500 pb-2">
              7. Sécurité des données
            </h2>
            <p className="text-gray-700">
              Nous mettons en œuvre des mesures techniques et organisationnelles appropriées pour protéger vos données personnelles 
              contre toute destruction, perte, altération, divulgation ou accès non autorisé. Nos données sont hébergées de manière 
              sécurisée et les accès sont strictement contrôlés.
            </p>
          </section>

          {/* Cookies */}
          <section className="mb-8">
            <h2 className="text-2xl font-bold text-slate-900 mb-4 border-b-2 border-orange-500 pb-2">
              8. Cookies
            </h2>
            <p className="text-gray-700 mb-3">
              Notre site utilise des cookies pour améliorer votre expérience de navigation et analyser le trafic. Les cookies utilisés sont :
            </p>
            <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
              <li><strong>Cookies essentiels :</strong> Nécessaires au bon fonctionnement du site</li>
              <li><strong>Cookies analytiques :</strong> Pour analyser l'utilisation du site (anonymisés)</li>
            </ul>
            <p className="text-gray-700 mt-3">
              Vous pouvez à tout moment désactiver les cookies dans les paramètres de votre navigateur.
            </p>
          </section>

          {/* Réclamation */}
          <section className="mb-8">
            <h2 className="text-2xl font-bold text-slate-900 mb-4 border-b-2 border-orange-500 pb-2">
              9. Droit de réclamation
            </h2>
            <p className="text-gray-700">
              Si vous estimez que vos droits ne sont pas respectés, vous avez le droit d'introduire une réclamation auprès de la 
              <strong> Commission Nationale de l'Informatique et des Libertés (CNIL)</strong> :
            </p>
            <div className="mt-4 p-4 bg-gray-50 rounded-lg">
              <p className="text-gray-700"><strong>Site web :</strong> <a href="https://www.cnil.fr" target="_blank" rel="noopener noreferrer" className="text-orange-500 hover:underline">www.cnil.fr</a></p>
              <p className="text-gray-700"><strong>Adresse :</strong> 3 Place de Fontenoy, 75007 Paris</p>
              <p className="text-gray-700"><strong>Téléphone :</strong> 01 53 73 22 22</p>
            </div>
          </section>

          {/* Modifications */}
          <section>
            <h2 className="text-2xl font-bold text-slate-900 mb-4 border-b-2 border-orange-500 pb-2">
              10. Modifications de la politique
            </h2>
            <p className="text-gray-700">
              Nous nous réservons le droit de modifier cette politique de confidentialité à tout moment. Toute modification sera 
              publiée sur cette page avec une nouvelle date de mise à jour. Nous vous encourageons à consulter régulièrement cette page.
            </p>
          </section>

          {/* Footer */}
          <div className="mt-12 pt-6 border-t border-gray-200 text-center text-sm text-gray-500">
            Dernière mise à jour : {new Date().toLocaleDateString('fr-FR', { year: 'numeric', month: 'long', day: 'numeric' })}
          </div>
        </div>
      </div>
      </div>
    </>
  );
};

export default PolitiqueConfidentialite;